import { forwardRef, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Internals, OffthreadVideo, RemotionMainVideoProps, RemotionVideoProps, Video } from "remotion";
import { BufferContext } from "./BufferManager";
import React from "react";
import { useSelector } from "react-redux";
import { GetAudioPlayMode, GetHandleAudio } from "../../Redux/Reducers/redux-slice-timetool";
import { useSetupVideoAudioContext } from "./audioModule/audio-helpers";
import Hls from "hls.js";
import { AudioChannel } from "./custom-timeline-action";
import { useTranslation } from "react-i18next";

export const HlsVideo: React.FC<RemotionVideoProps & { startFrom: number }> = ({ src, startFrom }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!src) {
      throw new Error(t("page:error.srcIsRequired"));
    }

    const startFrom = 0;

    const hls = new Hls({
      startLevel: 4,
      maxBufferLength: 5,
      maxMaxBufferLength: 5,
    });

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      hls.startLoad(startFrom);
    });

    hls.loadSource(src);
    hls.attachMedia(videoRef.current!);

    return () => {
      hls.destroy();
    };
  }, [src]);

  return <Video startFrom={startFrom} ref={videoRef} src={src} />;
};

const PausableVideoFunction: React.ForwardRefRenderFunction<
  HTMLVideoElement,
  RemotionVideoProps &
    RemotionMainVideoProps & { fps: number; videoFps: number; actionId: string; channelToPlay?: AudioChannel }
> = ({ src, fps, videoFps, actionId, channelToPlay, ...props }, ref) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const handleAudio = useSelector(GetHandleAudio);
  const audioPlayMode = useSelector(GetAudioPlayMode);

  const [hlsReady, setHlsReady] = useState(false);
  const [didSwitchAudioTrack, setdidSwitchAudioTrack] = useState(false);

  useEffect(() => {
    setdidSwitchAudioTrack(!handleAudio);
  }, [channelToPlay, src, channelToPlay?.trackNumber, channelToPlay?.channelPosition, handleAudio]);

  const hlsRef = useRef<Hls>(undefined);
  useEffect(() => {
    handleAudio && setdidSwitchAudioTrack(false);
    if (!src) {
      throw new Error(t("page:error.srcIsRequired"));
    }

    const startFrom = 0;

    hlsRef.current = new Hls({
      startLevel: 4,
      maxBufferLength: 5,
      maxMaxBufferLength: 5,
      maxBufferHole: 0,
      audioPreference: { channels: "16", groupId: "aac" },
      manifestLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: Infinity,
          maxLoadTimeMs: 20000,
          timeoutRetry: {
            maxNumRetry: 2,
            retryDelayMs: 2000,
            maxRetryDelayMs: 5000,
          },
          errorRetry: {
            maxNumRetry: 1,
            retryDelayMs: 5000,
            maxRetryDelayMs: 8000,
          },
        },
      },
    });

    hlsRef.current.on(Hls.Events.MEDIA_ATTACHED, (e, data) => {
      setHlsReady(true);
    });

    hlsRef.current.loadSource(src);
    hlsRef.current.attachMedia(videoRef.current!);

    return () => {
      hlsRef.current?.destroy();
    };
  }, [actionId, src, handleAudio]);

  const id = useId();

  useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement);

  const { canPlay, needsToBuffer, handleError } = useContext(BufferContext);

  const trackNumber = useMemo(() => channelToPlay?.trackNumber, [channelToPlay, channelToPlay?.trackNumber]);

  const playChannel = useMemo(
    () => (trackNumber && audioPlayMode === "mono" ? (trackNumber - 1) % 2 : 2),
    [trackNumber, audioPlayMode]
  );

  const audioSetup = useSetupVideoAudioContext(
    videoRef.current,
    playChannel,
    `${actionId}_${trackNumber}_${playChannel}`,
    handleAudio
  );

  useEffect(() => {
    if (!handleAudio && hlsReady && hlsRef.current) {
      hlsRef.current.audioTrack = 0;
      setdidSwitchAudioTrack(true);
      return;
    }
    if (!hlsReady || didSwitchAudioTrack) return;

    if (hlsRef.current && trackNumber !== undefined && trackNumber !== null && trackNumber >= 0) {
      hlsRef.current.audioTrack = Math.trunc((trackNumber - 1) / 2);
      setdidSwitchAudioTrack(true);
    } else {
      if (hlsRef.current) hlsRef.current.audioTrack = 0;
    }
  }, [trackNumber, hlsReady, didSwitchAudioTrack, handleAudio]);

  useEffect(() => {
    const { current } = videoRef;
    if (!current) {
      return;
    }
    const onError = (e) => {
      handleError(e.srcElement?.error?.message + " " + e.srcElement?.currentSrc);
    };

    const onPlay = () => {
      id && id !== "" && canPlay(id);
      id && id !== "" && handleAudio && setdidSwitchAudioTrack(false);
    };

    const onBuffer = () => {
      id && id !== "" && needsToBuffer(id);
    };

    current.addEventListener("canplay", onPlay);
    current.addEventListener("waiting", onBuffer);
    current.addEventListener("error", onError);

    return () => {
      current.removeEventListener("canplay", onPlay);
      current.removeEventListener("waiting", onBuffer);
      current.removeEventListener("error", onError);
    };
  }, [canPlay, id, needsToBuffer, handleAudio]);

  if (Internals.getRemotionEnvironment().isRendering) {
    return <OffthreadVideo muted {...props} src={src as string}></OffthreadVideo>;
  }

  return (
    <Video style={{ width: "100%", height: "100%" }} loop={false} {...props} ref={videoRef} src={src || ""}></Video>
  );
};

export const PausableVideo = forwardRef(PausableVideoFunction);
